// v3 executive deck: the hero number, then one slide per use case: technical win -> what changes ->
// business value on top, the customers who proved it underneath.
//
// Reads only a parsed ValueStory (math-free) through deck-view-model.ts — never the engine
// (calculator -> deck boundary, CLAUDE.md). Slides are fixed 1920x1080 inside the vendored
// <deck-stage>; the leave-behind PDF is window.print() and the ROI one-pager is the same print
// scoped to the first slide (`data-vc-roi-slide`, rules in styles.css), exactly as in v2.

import { useEffect, useMemo, type CSSProperties } from 'react';

import iconGrowth from '../../../assets/icons/Growth-chart.png';
import logoBlk from '../../../assets/logo/Clay_Logo_Primary_Blk.png';
import type { ValueStory } from '../../../engine/value-story-schema';
import { DeckStage } from '../../DeckStage';
import { Ph } from '../../Icon';
import {
  ESTIMATE_DISCLAIMER,
  buildDeckViewModel,
  type NumberSlideVm,
  type StoryCardVm,
  type UseCaseSlideVm,
} from './deck-view-model';
import { slabStyle } from '../slab';
import { PRINT_ROI_CLASS, printDeck, printRoiOnePager } from './print';

/* ── style helpers (v2 deck's visual language) ── */

const slide = (extra: CSSProperties = {}): CSSProperties => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  background: 'var(--oat-100)',
  color: 'var(--oat-500)',
  fontFamily: 'var(--font-body)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '80px 96px 130px',
  gap: '30px',
  ...extra,
});

const EYEBROW: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '24px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--oat-400)',
};

const SUBLABEL: CSSProperties = { ...EYEBROW, fontSize: '22px', letterSpacing: '0.1em' };

const H2: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '88px',
  fontWeight: 500,
  lineHeight: 0.98,
  letterSpacing: '-0.02em',
  margin: 0,
};

const MONO_NUM: CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '-0.01em' };

const FAB_PILL: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'var(--font-body)',
  fontSize: '13.5px',
  fontWeight: 500,
  padding: '10px 16px',
  borderRadius: '999px',
  background: 'var(--white)',
  border: '1px solid var(--grey-line)',
  color: 'var(--oat-500)',
  cursor: 'pointer',
  boxShadow: 'var(--shadow-sm)',
};

const DISCLAIMER: CSSProperties = {
  flex: 'none',
  margin: 0,
  fontSize: '22px',
  lineHeight: 1.4,
  color: 'var(--oat-400)',
};

/* ── slide 1: the number (hero) ── */

const SLAB_EYEBROW: CSSProperties = { ...EYEBROW, fontSize: '26px', color: 'inherit' };
/* The two supporting slabs share the hero's height, so their text is sized to fit it with the ROI
   line wrapping to two lines. */
const SUPPORT_NUMBER: CSSProperties = { fontFamily: 'var(--font-display)', fontWeight: 575, fontSize: '104px', lineHeight: 0.95, letterSpacing: '-0.04em' };
const SUPPORT_SUB: CSSProperties = { fontSize: '24px', fontWeight: 500, lineHeight: 1.25 };

function NumberSlide({ n, dateText }: { n: NumberSlideVm; dateText: string }) {
  // Hero number scales down for long figures so it stays on one line inside the slab.
  const heroSize = n.programText.length > 7 ? '210px' : n.programText.length > 5 ? '250px' : '290px';
  const plural = n.useCaseCount === 1 ? '' : 's';

  return (
    <section
      data-vc-roi-slide=""
      data-label="The number"
      data-screen-label="The number"
      data-speaker-notes="Total program value, the share Clay can credibly take credit for, and what that is worth against the Clay contract estimate."
      style={slide({ gap: '26px' })}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img src={logoBlk} alt="Clay" style={{ height: '48px', width: 'auto' }} />
        <span style={{ ...EYEBROW, fontSize: '22px' }}>
          {[n.customerName, dateText].filter(Boolean).join(' · ')}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <span style={EYEBROW}>The number</span>
        <h2 style={{ ...H2, fontSize: '72px' }}>What Clay is worth to {n.customerName || 'your team'}.</h2>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: '24px' }}>
        {/* Hero slab: total program value */}
        <div
          className="slab"
          data-om-raster=""
          style={slabStyle('lime', 300, {
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '44px 56px 40px',
          })}
        >
          <span style={SLAB_EYEBROW}>Total program value</span>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 575,
              fontSize: heroSize,
              lineHeight: 0.9,
              letterSpacing: '-0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {n.programText}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '34px', fontWeight: 500, letterSpacing: '-0.02em' }}>
              per year, across {n.useCaseCount} use case{plural}
            </span>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span className="slab" data-om-raster="" style={slabStyle('lemon', 200, { ...PILL })}>
                Gross {n.grossText} before realization
              </span>
              {n.pipelineText ? (
                <span className="slab" data-om-raster="" style={slabStyle('ube', 200, { ...PILL })}>
                  {n.pipelineText}
                </span>
              ) : null}
            </div>
          </div>
          <img
            src={iconGrowth}
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', right: '36px', top: '28px', width: '230px', height: '230px', objectFit: 'contain' }}
          />
        </div>

        {/* Supporting slabs: Clay-attributed value, then ROI */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', minHeight: 0 }}>
          <div
            className="slab"
            data-om-raster=""
            style={slabStyle('slushie', 200, { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '32px 44px' })}
          >
            <span style={SLAB_EYEBROW}>Clay-attributed value</span>
            <span style={SUPPORT_NUMBER}>
              {n.attributedText}
            </span>
            <span style={SUPPORT_SUB}>
              {n.attributionText ? `${n.attributionText} attributed to Clay, per year` : 'per year'}
            </span>
          </div>
          <div
            className="slab"
            data-om-raster=""
            style={slabStyle('lemon', 300, { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '32px 44px' })}
          >
            <span style={SLAB_EYEBROW}>ROI</span>
            {n.roiText ? (
              <>
                <span style={SUPPORT_NUMBER}>
                  {n.roiText}
                </span>
                <span style={SUPPORT_SUB}>Clay-attributed value ÷ {n.contractText} contract estimate</span>
              </>
            ) : (
              <span style={{ fontSize: '34px', fontWeight: 500, lineHeight: 1.15 }}>{n.roiAbsentCopy}</span>
            )}
          </div>
        </div>
      </div>

      <p style={{ ...DISCLAIMER, fontFamily: 'var(--font-mono)' }}>{n.formulaLabel}</p>
      <p style={DISCLAIMER}>{ESTIMATE_DISCLAIMER}</p>
    </section>
  );
}

const PILL: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '24px',
  fontWeight: 600,
  padding: '10px 26px',
  borderRadius: '999px',
  whiteSpace: 'nowrap',
};

/* ── slides 2+: one slide per use case — technical win → what changes → business value, then the
      customers who proved it ── */

// Technical win, arrow, what changes, arrow, business value.
const HERO_COLS = '1fr 56px 1.4fr 56px 1fr';

const COL_HEAD: CSSProperties = { ...SUBLABEL, padding: '0 32px' };

function Arrow() {
  return (
    <span aria-hidden style={{ ...MONO_NUM, fontSize: '44px', color: 'var(--blueberry-300)', alignSelf: 'center', textAlign: 'center' }}>
      →
    </span>
  );
}

/* One text size per slab type, so no slide is tighter than another. Sized to fit the longest default
   copy of every use case in the hero's fixed height (measured at 1920x1080 with all twelve selected).
   A slide with no customer stories lets the hero grow and steps the type up (`*_BIG`). */
const HERO_SLAB_HEIGHT = '296px';
const HERO_CELL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '12px',
  padding: '24px 36px',
  borderRadius: '44px',
  minWidth: 0,
  overflow: 'hidden',
};
const WIN_NAME: CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '24px', lineHeight: 1.2 };
const WIN_VALUE: CSSProperties = { fontFamily: 'var(--font-display)', fontWeight: 575, fontSize: '56px', lineHeight: 1.05, letterSpacing: '-0.03em' };
const CHANGE_TEXT: CSSProperties = { fontSize: '32px', lineHeight: 1.28, fontWeight: 450, textWrap: 'pretty' };
const VALUE_PHRASE: CSSProperties = { fontSize: '27px', lineHeight: 1.2, fontWeight: 500, textWrap: 'pretty' };
const VALUE_AMOUNT: CSSProperties = { ...MONO_NUM, fontSize: '60px', fontWeight: 700, lineHeight: 1.05 };

const BIG = {
  winName: { fontSize: '30px' },
  winValue: { fontSize: '80px' },
  change: { fontSize: '42px' },
  phrase: { fontSize: '34px' },
  amount: { fontSize: '84px' },
} satisfies Record<string, CSSProperties>;

const STORY_SLAB_HEIGHT = '264px';
const STORY_CARD: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  height: STORY_SLAB_HEIGHT,
  padding: '26px 32px',
  borderRadius: '40px',
  minWidth: 0,
  overflow: 'hidden',
  background: 'var(--white)',
  border: '1px solid var(--grey-line)',
  color: 'var(--oat-500)',
};
const STORY_CUSTOMER: CSSProperties = { fontSize: '30px', lineHeight: 1.15, fontWeight: 600 };
const STORY_METRIC: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: '32px', fontWeight: 600, lineHeight: 1.2, color: 'var(--tangerine-400)' };
const STORY_NOTE: CSSProperties = { fontSize: '24px', lineHeight: 1.3, fontWeight: 450, color: 'var(--oat-400)', textWrap: 'pretty' };

function StoryCard({ story }: { story: StoryCardVm }) {
  return (
    <div className="slab" data-om-raster="" data-vc-story="" style={STORY_CARD}>
      <span style={STORY_CUSTOMER}>{story.customer}</span>
      {story.metric ? <span style={STORY_METRIC}>{story.metric}</span> : null}
      {story.note ? <span style={STORY_NOTE}>{story.note}</span> : null}
    </div>
  );
}

function UseCaseSlide({ slideVm }: { slideVm: UseCaseSlideVm }) {
  const { row, stories, index, count } = slideVm;
  const big = stories.length === 0;
  const heroCell: CSSProperties = big ? { ...HERO_CELL, flex: 1 } : { ...HERO_CELL, height: HERO_SLAB_HEIGHT };
  return (
    <section
      data-label={row.name}
      data-screen-label={row.name}
      data-speaker-notes="The technical win the customer's own inputs produce, what changes for the team (the same for every customer), the program value it adds up to, and the customers who proved it."
      style={slide({ gap: '16px', paddingTop: '64px', paddingBottom: '120px' })}
    >
      <span style={EYEBROW}>Use case {index} of {count}</span>
      <h2 style={{ ...H2, fontSize: '72px' }}>{row.name}</h2>

      <div style={{ display: 'grid', gridTemplateColumns: HERO_COLS, marginTop: '4px' }} aria-hidden>
        <span style={{ ...COL_HEAD, color: 'var(--tangerine-400)' }}>Technical win</span>
        <span />
        <span style={COL_HEAD}>What changes for you</span>
        <span />
        <span style={{ ...COL_HEAD, color: 'var(--blueberry-400)' }}>Business value</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: HERO_COLS, ...(big ? { flex: 1, minHeight: 0 } : {}) }}>
        <div className="slab" data-om-raster="" data-vc-hero="" style={slabStyle('tangerine', 200, heroCell)}>
          {row.winName ? <span style={{ ...WIN_NAME, ...(big ? BIG.winName : {}) }}>{row.winName}</span> : null}
          <span style={{ ...WIN_VALUE, ...(big ? BIG.winValue : {}) }}>{row.winValue}</span>
        </div>
        <Arrow />
        <div className="slab" data-om-raster="" data-vc-hero="" style={slabStyle('oat', 200, heroCell)}>
          <span style={{ ...CHANGE_TEXT, ...(big ? BIG.change : {}) }}>{row.changeText}</span>
        </div>
        <Arrow />
        <div className="slab" data-om-raster="" data-vc-hero="" style={slabStyle(row.tone, 200, heroCell)}>
          {row.valuePhrase ? <span style={{ ...VALUE_PHRASE, ...(big ? BIG.phrase : {}) }}>{row.valuePhrase}</span> : null}
          <span style={{ ...VALUE_AMOUNT, ...(big ? BIG.amount : {}) }}>{row.valueAmountText}</span>
        </div>
      </div>

      {stories.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '6px' }}>
          <span style={SUBLABEL}>Customers who proved it</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {stories.map((st) => (
              <StoryCard key={`${st.customer}|${st.metric}|${st.note}`} story={st} />
            ))}
          </div>
        </div>
      ) : null}

      {big ? null : <div style={{ flex: 1 }} />}
      <p style={DISCLAIMER}>{ESTIMATE_DISCLAIMER}</p>
    </section>
  );
}

/* ── the deck ── */

export interface ValueStoryDeckV3Props {
  /** A parsed v3 story (ValueStorySchema, `v: 2`). */
  story: ValueStory;
  onBack: () => void;
}

export function ValueStoryDeckV3({ story, onBack }: ValueStoryDeckV3Props) {
  const vm = useMemo(() => buildDeckViewModel(story), [story]);

  // The ROI-only print scope is a body class; it must not outlive the route (see print.ts).
  useEffect(() => () => document.body.classList.remove(PRINT_ROI_CLASS), []);

  return (
    <div className="vc-root">
      <DeckStage width={1920} height={1080}>
        <NumberSlide n={vm.number} dateText={vm.dateText} />
        {vm.useCaseSlides.map((s) => (
          <UseCaseSlide key={`uc-${s.row.id}`} slideVm={s} />
        ))}
      </DeckStage>

      <button
        type="button"
        id="vs-back-fab"
        onClick={onBack}
        className="vc-hov-bg-oat200"
        // Top right: the thumbnail rail owns the left edge of the screen.
        style={{ position: 'fixed', top: '18px', right: '18px', zIndex: 1000, ...FAB_PILL }}
      >
        <Ph name="arrow-left" size={16} style={{ color: 'var(--oat-400)' }} />
        Back to calculator
      </button>

      <div
        id="vs-export-fab"
        style={{
          position: 'fixed',
          bottom: '18px',
          left: '18px',
          zIndex: 1000,
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <button type="button" onClick={() => void printDeck()} className="vc-hov-bg-oat200" style={FAB_PILL}>
          <Ph name="file-pdf" size={16} style={{ color: 'var(--oat-400)' }} />
          Download PDF
        </button>
        <button type="button" onClick={() => void printRoiOnePager()} className="vc-hov-bg-oat200" style={FAB_PILL}>
          <Ph name="presentation" size={16} style={{ color: 'var(--oat-400)' }} />
          ROI one-pager (PDF)
        </button>
      </div>
    </div>
  );
}
